/**
 * File Menu Component
 * 
 * Provides project management options:
 * - New Project
 * - Open Project
 * - Save / Save As
 * - Rename Project
 * - Auto-save indicator
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  CircularProgress,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { AutoSaveStatus } from '../types/project';

interface FileMenuProps {
  projectName: string;
  isDirty: boolean;
  autoSaveStatus: AutoSaveStatus;
  lastSavedAt: Date | null;
  onNewProject: () => void;
  onOpenProject: () => void;
  onSave: () => void;
  onSaveAs: () => void;
  onRename: (newName: string) => void;
}

export const FileMenu: React.FC<FileMenuProps> = ({
  projectName,
  isDirty,
  autoSaveStatus,
  lastSavedAt,
  onNewProject,
  onOpenProject,
  onSave,
  onSaveAs,
  onRename,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newName, setNewName] = useState(projectName);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    if (newName.trim() && newName.trim() !== projectName) {
      onRename(newName.trim());
    }
    setShowRenameDialog(false);
  };

  // Auto-save status indicator
  const getStatusIcon = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return <SyncIcon sx={{ fontSize: 16, color: '#F59E0B', animation: 'spin 1s linear infinite' }} />;
      case 'saved':
        return <CloudDoneIcon sx={{ fontSize: 16, color: '#10B981' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 16, color: '#EF4444' }} />;
      default:
        return isDirty ? <CloudOffIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /> : null;
    }
  };

  const getStatusText = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return lastSavedAt ? `Saved ${formatTimeAgo(lastSavedAt)}` : 'Saved';
      case 'error':
        return 'Save failed';
      default:
        return isDirty ? 'Unsaved changes' : '';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Project Name / File Menu Button */}
        <Button
          onClick={handleMenuOpen}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            textTransform: 'none',
            color: '#374151',
            fontWeight: 600,
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.04)',
            },
          }}
        >
          <FolderIcon sx={{ mr: 1, fontSize: 18, color: '#F59E0B' }} />
          {projectName || 'Untitled Project'}
          {isDirty && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                color: '#9CA3AF',
              }}
            >
              •
            </Box>
          )}
        </Button>

        {/* Auto-save Status */}
        <Tooltip title={getStatusText()}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#9CA3AF', fontSize: '0.75rem' }}>
            {getStatusIcon()}
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {getStatusText()}
            </Typography>
          </Box>
        </Tooltip>

        {/* File Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { minWidth: 200 },
          }}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onNewProject();
            }}
          >
            <ListItemIcon>
              <NoteAddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>New Project</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleMenuClose();
              onOpenProject();
            }}
          >
            <ListItemIcon>
              <FolderOpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open Project</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              handleMenuClose();
              onSave();
            }}
          >
            <ListItemIcon>
              <SaveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              ⌘S
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleMenuClose();
              onSaveAs();
            }}
          >
            <ListItemIcon>
              <SaveAsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save As...</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              handleMenuClose();
              setNewName(projectName);
              setShowRenameDialog(true);
            }}
          >
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename Project</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Rename Dialog */}
      <Dialog
        open={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Project Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRenameDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRename}
            disabled={!newName.trim() || newName.trim() === projectName}
            sx={{ textTransform: 'none' }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS for spinning animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default FileMenu;
